import { zodResolver } from '@hookform/resolvers/zod';
import { Autocomplete, Box, Button, CircularProgress, Grid, Stack, TextField, Typography } from '@mui/material';
import { ContactVM, FacultyIM, FacultyIMSchema } from '@tto/shared-models';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { useGetUnassignedContactsQuery } from 'src/services/restApi/queries/contact.service';
import { useCreateFacultyMutation } from 'src/services/restApi/queries/faculty.service';
import { useDialogStore } from 'src/store/dialog/dialogStore';

export function CreateFacultyModal() {
  const [createFaculty, { isLoading }] = useCreateFacultyMutation();
  const {
    data: fetchedUnassignedContacts,
    refetch: refetchUnassigned,
    isLoading: isLoadingUnassigned,
  } = useGetUnassignedContactsQuery();
  const { closeDialog, openDialog } = useDialogStore((state) => state);

  const onSubmit = async (data: FacultyIM) => {
    try {
      console.log(data);
      await createFaculty(FacultyIMSchema.parse(data)).unwrap();
    } catch (err) {
      toast.error('Faculty creation failed. Please try again later.');
    }

    closeDialog();
  };

  const { handleSubmit, control, register, formState } = useForm<FacultyIM>({
    resolver: zodResolver(FacultyIMSchema),
  });

  return (
    <Box>
      <Typography paddingBottom={2} align={'center'} variant='h3'>
        Add a Faculty
      </Typography>
      {isLoadingUnassigned ? (
        <Box padding={6}>
          <SuspenseLoader />
        </Box>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} paddingBottom={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label='Unique Identifier'
                {...register('id')}
                error={!!formState.errors.name}
                helperText={typeof formState.errors.id?.message === 'string' ? formState.errors.id.message : undefined}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size='small'
                label='Name'
                {...register('name')}
                error={!!formState.errors.name}
                helperText={
                  typeof formState.errors.name?.message === 'string' ? formState.errors.name.message : undefined
                }
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Controller
                name='contactId'
                control={control}
                render={({ field }) => (
                  <Autocomplete<ContactVM>
                    options={fetchedUnassignedContacts ?? []}
                    value={fetchedUnassignedContacts?.find((c) => c.id === field.value) ?? null}
                    getOptionLabel={(option) => option.name}
                    onChange={(_, data) => {
                      field.onChange(data?.id ?? null);
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Assign Contact'
                        size='small'
                        error={!!formState.errors.contactId}
                        helperText={formState.errors.contactId ? formState.errors.contactId.message : null}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Stack direction='row' justifyContent={'flex-end'} paddingTop={1}>
            <Button onClick={() => closeDialog()}>Cancel</Button>
            <Button
              variant={'contained'}
              type='submit'
              startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Submit'}
            </Button>
          </Stack>
        </form>
      )}
    </Box>
  );
}
